import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { asArray, firstRecord, formatCurrency, unwrapData } from "../lib/catalog";
import CatalogImage from "../components/common/CatalogImage";
import PageLoader from "../components/common/PageLoader";

function pickTariff(payload, tariffId) {
  const data = unwrapData(payload);
  const list = asArray(data);
  if (list.length > 0) {
    return list.find((item) => String(item?.tariff?.tariff_id || item?.tariff_id) === String(tariffId)) || null;
  }
  if (data && (data.tariff_id || data.tariff)) {
    return data;
  }
  return null;
}

export default function TransportationDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function loadTariff() {
      try {
        const listPayload = await agentApi.get("/transport/tariff");
        let matched = pickTariff(listPayload, id);

        if (!matched) {
          const detailPayload = await agentApi.get(`/transport/tariff/${encodeURIComponent(id)}`);
          matched = pickTariff(detailPayload, id);
        }

        if (!matched) {
          throw Object.assign(new Error("Transport tariff not found."), { status: 404 });
        }
        const tariff = matched?.tariff || matched || {};
        let vehicle = firstRecord(matched?.vehicle);
        const city = firstRecord(matched?.city);
        const type = firstRecord(matched?.type);
        const pkg = firstRecord(matched?.package);
        const vehicleId = vehicle?.vehicle_id || tariff.tariff_vehicle;

        if (!vehicle && vehicleId) {
          const vehiclePayload = await agentApi.get(`/transport/vehicles/${vehicleId}`);
          vehicle = unwrapData(vehiclePayload);
        }

        if (!alive) return;
        setItem({
          tariff,
          vehicle: vehicle || {},
          city: city || {},
          type: type || {},
          package: pkg || {},
        });
      } catch (fetchError) {
        if (!alive) return;
        showApiError(fetchError, "LOAD_TRANSPORT_DETAIL");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadTariff();

    return () => {
      alive = false;
    };
  }, [id]);

  const vehicle = item?.vehicle || {};
  const tariff = item?.tariff || {};
  const city = item?.city || {};
  const type = item?.type || {};
  const pkg = item?.package || {};

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Transportation detail"
          title={vehicle.vehicle_name || "Live transport tariff"}
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Transport", href: "/transportation" },
            { label: vehicle.vehicle_name || "Transport details" },
          ]}
          backTo={true}
        />

        {loading ? <PageLoader message="Loading transportation details..." /> : null}

        {!loading && item ? (
          <div className="row g-4">
            <div className="col-12 col-xl-5">
              <div className="card border-0 shadow-sm h-100 catalog-card">
                <CatalogImage folder="vehicle" fileName={vehicle.vehicle_photo} alt={vehicle.vehicle_name} height={320} />
                <div className="card-body">
                  <h4 className="mb-3">{vehicle.vehicle_name || "Vehicle"}</h4>
                  <div className="row g-3 small">
                    <div className="col-6"><strong>Passengers</strong><br />{vehicle.vehicle_passenger || "Not set"}</div>
                    <div className="col-6"><strong>Baggage</strong><br />{vehicle.vehicle_baggage || "Not set"}</div>
                    <div className="col-6"><strong>Fuel</strong><br />{vehicle.vehicle_fuel || "Not set"}</div>
                    <div className="col-6"><strong>Model</strong><br />{vehicle.vehicle_model || "Not set"}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-7">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-3">Tariff</h5>
                  <div className="row g-3 small">
                    <div className="col-md-6"><strong>Amount</strong><br />{formatCurrency(tariff.tariff_amount, "INR")}</div>
                    <div className="col-md-6"><strong>City</strong><br />{city.city_name || city.name || "Not set"}</div>
                    <div className="col-md-6"><strong>Type</strong><br />{type.price_tariff_plan || type.tariff_name || "Not set"}</div>
                    <div className="col-md-6"><strong>Package</strong><br />{pkg.price_tariff_plan || pkg.tariff_name || "Not set"}</div>
                  </div>
                  <hr className="my-4" />
                  <div className="small text-secondary">
                    This is the public transport catalogue. Standalone transport booking is not available on the current agent APIs.
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
